import React, { useState } from 'react';
import { FiChevronDown, FiAlertCircle, FiArrowUp, FiCheckCircle, FiExternalLink } from 'react-icons/fi';
import IconWrapper from '../IconWrapper';
import { useFramework } from '../../contexts/FrameworkContext';

type InsightType = 'critical' | 'emerging' | 'positive';

interface InsightProps {
  id: string;
  type: InsightType;
  title: string;
  rootCause: string;
  recommendedAction: string;
  impact: string;
  framework: 'NCD' | 'TCF';
  frameworkDetail: string;
  onViewDetails: (id: string) => void;
}

const InsightItem: React.FC<InsightProps> = ({ 
  id, 
  type, 
  title, 
  rootCause, 
  recommendedAction, 
  impact, 
  framework, 
  frameworkDetail,
  onViewDetails
}) => {
  const getIcon = () => {
    switch (type) {
      case 'critical':
        return <IconWrapper icon={FiAlertCircle} className="text-amber-500" size={24} />;
      case 'emerging':
        return <IconWrapper icon={FiArrowUp} className="text-blue-500" size={24} />;
      case 'positive':
        return <IconWrapper icon={FiCheckCircle} className="text-green-500" size={24} />;
      default:
        return null;
    }
  };

  const getTypeLabel = () => {
    switch (type) {
      case 'critical':
        return 'CRITICAL INSIGHT';
      case 'emerging':
        return 'EMERGING TREND';
      case 'positive':
        return 'POSITIVE TREND';
      default:
        return '';
    }
  };

  const getTypeClass = () => {
    switch (type) {
      case 'critical':
        return 'border-amber-200 bg-amber-50';
      case 'emerging':
        return 'border-blue-200 bg-blue-50';
      case 'positive':
        return 'border-green-200 bg-green-50';
      default:
        return '';
    }
  };

  return (
    <div className={`border rounded-lg p-4 mb-2 ${getTypeClass()}`}>
      <div className="flex justify-between mb-2">
        <div className="flex items-center">
          {getIcon()}
          <span className="ml-2 font-bold text-sm">{getTypeLabel()}</span>
        </div>
        <div className="text-xs text-secondary/70 font-medium">
          {framework === 'TCF' ? 'TCF OUTCOME' : 'NCD PRINCIPLE'}: {frameworkDetail}
        </div>
      </div>
      
      <div className="text-base font-medium mb-1">{title}</div>
      <div className="text-sm text-secondary/80 mb-3">Root Cause: {rootCause}</div>
      
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-3 mb-3">
        <div className="flex items-start mb-2">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary flex items-center justify-center text-secondary font-bold text-xs">
            1
          </div>
          <div className="ml-2 flex-1">
            <div className="text-sm font-medium">
              {recommendedAction}
            </div>
            <div className="flex items-center mt-1">
              <div className="text-xs font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded">
                {impact}
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end mt-1">
          <button className="text-xs text-primary hover:text-primary-dark px-2 py-0.5 border border-primary rounded hover:bg-primary/5 transition-colors">
            Assign
          </button>
        </div>
      </div>
      
      <div className="flex justify-end">
        <button 
          onClick={() => onViewDetails(id)}
          className="text-sm text-primary hover:underline flex items-center"
        >
          View Details
        </button>
        <span className="mx-2 text-secondary/30">|</span>
        <button className="text-sm text-primary hover:underline">
          Assign
        </button>
      </div>
    </div>
  );
};

// Mock data for insights
const mockInsights: {
  id: string;
  type: InsightType;
  title: string;
  rootCause: string;
  recommendedAction: string;
  impact: string;
  framework: 'NCD' | 'TCF';
  frameworkDetail: string;
}[] = [
  {
    id: 'INS-001',
    type: 'critical' as InsightType,
    title: '73% increase in complaints about unclear fee disclosures in new accounts',
    rootCause: 'Recent changes to fee structure not clearly communicated',
    recommendedAction: 'Review onboarding materials and update fee section',
    impact: 'Affects approximately 1,200 new customers in past 30 days',
    framework: 'TCF',
    frameworkDetail: 'OUTCOME 3: CLEAR INFORMATION'
  },
  {
    id: 'INS-002',
    type: 'emerging' as InsightType,
    title: '41% increase in complaints about account closure difficulty',
    rootCause: 'New verification steps adding friction to closure process',
    recommendedAction: 'Simplify verification while maintaining security',
    impact: 'Average resolution time increased from 2 to 5 days',
    framework: 'TCF',
    frameworkDetail: 'OUTCOME 6: NO UNREASONABLE BARRIERS'
  },
  {
    id: 'INS-003',
    type: 'positive' as InsightType,
    title: '37% decrease in mobile app stability complaints following recent update',
    rootCause: 'Focused improvements on most reported crash scenarios',
    recommendedAction: 'Continue monitoring less common crash scenarios',
    impact: 'Improved experience for approximately 85% of app users',
    framework: 'TCF',
    frameworkDetail: 'OUTCOME 5: PRODUCT PERFORMANCE'
  },
  {
    id: 'INS-004',
    type: 'critical' as InsightType,
    title: '67% increase in complaints about unclear terms and conditions',
    rootCause: 'Recent legal updates to T&Cs not simplified for customers',
    recommendedAction: 'Create plain language summaries of key terms',
    impact: 'Affects approximately 800 new customers in past 30 days',
    framework: 'NCD',
    frameworkDetail: 'CONSUMER UNDERSTANDING'
  },
  {
    id: 'INS-005',
    type: 'emerging' as InsightType,
    title: '38% increase in complaints about slow transaction processing',
    rootCause: 'Recent backend system upgrade affecting processing times',
    recommendedAction: 'Optimize database queries and increase capacity',
    impact: 'Average transaction time increased from 3 to 7 seconds',
    framework: 'NCD',
    frameworkDetail: 'CONSUMER SUPPORT'
  }
];

interface InsightsPanelProps {
  onViewDetails: (id: string) => void;
}

const InsightsPanel: React.FC<InsightsPanelProps> = ({ onViewDetails }) => {
  const { framework } = useFramework();
  const [isOpen, setIsOpen] = useState(true);

  // Filter insights based on current framework
  const filteredInsights = mockInsights.filter(insight => 
    insight.framework === framework
  );

  return (
    <div className="bg-white rounded-xl shadow-sm p-5 mb-6">
      <div className="flex justify-between items-center mb-4">
        <div className="font-bold text-lg flex items-center">
          REGULATORY INSIGHTS
          <button 
            onClick={() => setIsOpen(!isOpen)}
            className="ml-2 text-secondary/70 hover:text-secondary transition-colors"
          >
            <IconWrapper icon={FiChevronDown} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
        <div className="flex items-center">
          <button className="text-sm text-primary hover:underline mr-4">
            View All
          </button>
          <div className="flex items-center bg-gray-100 rounded-lg px-3 py-1">
            <span className="text-sm font-medium mr-2">▼</span>
            <span className="text-sm">{framework}</span>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="space-y-2">
          {filteredInsights.length > 0 ? (
            filteredInsights.map(insight => (
              <InsightItem
                key={insight.id}
                id={insight.id}
                type={insight.type}
                title={insight.title}
                rootCause={insight.rootCause}
                recommendedAction={insight.recommendedAction}
                impact={insight.impact}
                framework={insight.framework}
                frameworkDetail={insight.frameworkDetail}
                onViewDetails={onViewDetails}
              />
            ))
          ) : (
            <div className="text-center py-6 text-secondary/70">
              No insights available for the current framework
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InsightsPanel;